import React, { useEffect, useMemo, useRef, useState } from "react";
import DashboardLayout from "../components/dashboard/DashboardLayout";
import { Card } from "../components/ui/Card";
import LiveCandlesChart from "../components/tradingbot/LiveCandlesChart";
import { useTrading } from "../context/TradingContext";
import {
  createMarketStream,
  normalizeMarketInterval,
  toNormalizedCandle,
} from "../services/trading/binanceStream";
import { intervalToSeconds } from "../services/trading/indicators";

const MARKET_INTERVALS = {
  "15m": "15m",
  "1H": "1h",
  "4H": "4h",
  "1D": "1d",
};

const MARKET_POPULARITY_ORDER = [
  "BTCUSDT",
  "ETHUSDT",
  "SOLUSDT",
  "BNBUSDT",
  "ADAUSDT",
];

const formatMoney = (value) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(value || 0);

const normalizeCandles = (candles) => {
  const byTime = new Map();
  candles.forEach((candle) => byTime.set(candle.time, candle));
  return [...byTime.values()].sort((left, right) => left.time - right.time).slice(-240);
};

const seedCandles = (market, interval) => {
  const candles = [];
  const stepSeconds = intervalToSeconds(normalizeMarketInterval(interval));
  const nowSeconds = Math.floor(Date.now() / 1000);
  const alignedNow = nowSeconds - (nowSeconds % stepSeconds);
  const basePrice = market.price || 100;
  const marketVolatility = Math.max(basePrice * 0.0035, 0.01);
  let price = basePrice;

  for (let index = 140; index >= 0; index -= 1) {
    const time = alignedNow - index * stepSeconds;
    const drift = (Math.random() - 0.5) * marketVolatility;
    const open = price;
    const close = Math.max(0.0001, open + drift);
    candles.push({
      time,
      open,
      high: Math.max(open, close) + Math.random() * marketVolatility * 0.4,
      low: Math.min(open, close) - Math.random() * marketVolatility * 0.4,
      close,
      volume: 100 + Math.random() * 1200,
    });
    price = close;
  }

  return normalizeCandles(candles);
};

const Markets = () => {
  const {
    marketData,
    selectedPair,
    setSelectedPair,
    portfolio,
    upsertMarketData,
  } = useTrading();
  const [timeframe, setTimeframe] = useState("1H");
  const [candles, setCandles] = useState(() =>
    seedCandles(
      {
        ...selectedPair,
        price: selectedPair.price || 100,
      },
      MARKET_INTERVALS["1H"]
    )
  );
  const selectedStreamRef = useRef(null);
  const marketStreamsRef = useRef([]);

  const sortedMarkets = useMemo(
    () =>
      [...marketData].sort((left, right) => {
        const leftRank = MARKET_POPULARITY_ORDER.indexOf(left.symbol);
        const rightRank = MARKET_POPULARITY_ORDER.indexOf(right.symbol);
        const safeLeftRank = leftRank === -1 ? Number.MAX_SAFE_INTEGER : leftRank;
        const safeRightRank = rightRank === -1 ? Number.MAX_SAFE_INTEGER : rightRank;

        if (safeLeftRank !== safeRightRank) {
          return safeLeftRank - safeRightRank;
        }

        return right.volume24h - left.volume24h;
      }),
    [marketData]
  );

  const selectedInterval = MARKET_INTERVALS[timeframe] || "1h";

  useEffect(() => {
    marketStreamsRef.current.forEach((stream) => stream.close());
    marketStreamsRef.current = marketData.map((market) =>
      createMarketStream({
        symbol: market.symbol,
        interval: "1m",
        onTicker: (ticker) => {
          upsertMarketData({
            symbol: market.symbol,
            displaySymbol: market.displaySymbol,
            name: market.name,
            price: ticker.lastPrice,
            change24h: ticker.priceChangePercent,
            volume24h: ticker.volume,
          });
        },
      })
    );

    return () => {
      marketStreamsRef.current.forEach((stream) => stream.close());
      marketStreamsRef.current = [];
    };
  }, [marketData.length, upsertMarketData]);

  useEffect(() => {
    if (!selectedPair?.symbol) {
      return undefined;
    }

    selectedStreamRef.current?.close();
    setCandles(
      seedCandles(
        {
          ...selectedPair,
          price: selectedPair.price || 100,
        },
        selectedInterval
      )
    );

    selectedStreamRef.current = createMarketStream({
      symbol: selectedPair.symbol,
      interval: selectedInterval,
      onTicker: (ticker) => {
        upsertMarketData({
          symbol: selectedPair.symbol,
          displaySymbol: selectedPair.displaySymbol,
          name: selectedPair.name,
          price: ticker.lastPrice,
          change24h: ticker.priceChangePercent,
          volume24h: ticker.volume,
        });

        setCandles((previous) => {
          if (!previous.length) {
            return previous;
          }

          const next = [...previous];
          const last = next[next.length - 1];
          next[next.length - 1] = {
            ...last,
            close: ticker.lastPrice,
            high: Math.max(last.high, ticker.lastPrice),
            low: Math.min(last.low, ticker.lastPrice),
            volume: ticker.volume,
          };
          return normalizeCandles(next);
        });
      },
      onKline: (kline) => {
        setCandles((previous) => {
          const candle = toNormalizedCandle(kline);
          const next = [...previous];
          const existingIndex = next.findIndex((entry) => entry.time === candle.time);

          if (existingIndex === -1) {
            next.push(candle);
          } else {
            next[existingIndex] = candle;
          }

          return normalizeCandles(next);
        });
      },
    });

    return () => {
      selectedStreamRef.current?.close();
    };
  }, [
    selectedInterval,
    selectedPair.displaySymbol,
    selectedPair.name,
    selectedPair.price,
    selectedPair.symbol,
    upsertMarketData,
  ]);

  return (
    <DashboardLayout>
      <div className="space-y-5 p-4 sm:space-y-6 sm:p-6 lg:p-8">
        <div className="overflow-hidden rounded-[1.75rem] border border-slate-800 bg-[radial-gradient(circle_at_top_left,_rgba(34,211,238,0.18),_transparent_28%),linear-gradient(135deg,#020617,#0f172a_52%,#0b1120)] p-5 text-white shadow-2xl shadow-cyan-950/20 sm:rounded-[2rem] sm:p-7">
          <div className="grid gap-6 xl:grid-cols-[1.15fr_0.85fr] xl:items-end">
            <div>
              <div className="inline-flex rounded-full border border-cyan-400/30 bg-cyan-400/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.24em] text-cyan-200">
                Live Markets
              </div>
              <h1 className="mt-4 text-3xl font-semibold tracking-tight xl:text-4xl">Markets</h1>
              <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-300">
                Real-time market watchlist, continuously updating price action, and live chart
                analysis for the most active instruments.
              </p>
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                <div className="text-xs uppercase tracking-[0.18em] text-slate-400">Selected Pair</div>
                <div className="mt-2 text-2xl font-semibold text-white">{selectedPair.displaySymbol}</div>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                <div className="text-xs uppercase tracking-[0.18em] text-slate-400">Last Price</div>
                <div className="mt-2 text-2xl font-semibold text-cyan-300">{formatMoney(selectedPair.price)}</div>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                <div className="text-xs uppercase tracking-[0.18em] text-slate-400">24H Change</div>
                <div className={`mt-2 text-2xl font-semibold ${selectedPair.change24h >= 0 ? "text-emerald-300" : "text-rose-300"}`}>
                  {selectedPair.change24h.toFixed(2)}%
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid gap-4 sm:gap-6 xl:grid-cols-[1.15fr_0.85fr]">
          <Card className="border-slate-200 bg-white/90 shadow-sm dark:border-slate-800 dark:bg-slate-950/80">
            <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <div className="text-xs font-medium uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                  Selected market
                </div>
                <div className="mt-2 text-4xl font-semibold tracking-tight text-slate-950 dark:text-white">
                  {selectedPair.displaySymbol}
                </div>
                <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">{selectedPair.name}</div>
              </div>
              <div className="flex gap-2">
                {["15m", "1H", "4H", "1D"].map((interval) => (
                  <button
                    key={interval}
                    onClick={() => setTimeframe(interval)}
                    className={`rounded-xl px-3 py-2 text-sm transition ${
                      timeframe === interval
                        ? "bg-cyan-500 text-slate-950"
                        : "border border-slate-200 text-slate-600 hover:border-slate-400 dark:border-slate-800 dark:text-slate-300"
                    }`}
                  >
                    {interval}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              <div className="rounded-2xl border border-slate-200 bg-slate-50/90 p-4 dark:border-slate-800 dark:bg-slate-900/70">
                <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Last price</div>
                <div className="mt-2 text-2xl font-semibold text-slate-950 dark:text-white">
                  {formatMoney(selectedPair.price)}
                </div>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50/90 p-4 dark:border-slate-800 dark:bg-slate-900/70">
                <div className="text-xs uppercase tracking-[0.18em] text-slate-500">24h change</div>
                <div
                  className={`mt-2 text-2xl font-semibold ${
                    selectedPair.change24h >= 0 ? "text-emerald-500" : "text-rose-500"
                  }`}
                >
                  {selectedPair.change24h.toFixed(2)}%
                </div>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50/90 p-4 dark:border-slate-800 dark:bg-slate-900/70">
                <div className="text-xs uppercase tracking-[0.18em] text-slate-500">24h volume</div>
                <div className="mt-2 text-2xl font-semibold text-slate-950 dark:text-white">
                  {formatMoney(selectedPair.volume24h)}
                </div>
              </div>
            </div>

            <div className="mt-6 rounded-3xl border border-slate-200 bg-slate-50/90 p-4 shadow-inner dark:border-slate-800 dark:bg-slate-900/50">
              <LiveCandlesChart candles={candles} markers={[]} />
            </div>

            <div className="mt-6 grid gap-4 md:grid-cols-2">
              <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950/70">
                <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Realized portfolio</div>
                <div className="mt-2 text-2xl font-semibold text-slate-950 dark:text-white">
                  {formatMoney(portfolio.totalValue)}
                </div>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950/70">
                <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Realized total P&L</div>
                <div
                  className={`mt-2 text-2xl font-semibold ${
                    portfolio.totalPnL >= 0 ? "text-emerald-500" : "text-rose-500"
                  }`}
                >
                  {formatMoney(portfolio.totalPnL)}
                </div>
              </div>
            </div>
          </Card>

          <Card className="border-slate-200 bg-white/90 p-6 shadow-sm dark:border-slate-800 dark:bg-slate-950/80">
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-slate-950 dark:text-white">Market List</h2>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                Ordered by popularity first, then live volume.
              </p>
            </div>
            <div className="mt-5 space-y-3">
              {sortedMarkets.map((market) => (
                <button
                  key={market.symbol}
                  onClick={() => setSelectedPair(market)}
                  className={`w-full rounded-2xl border px-4 py-4 text-left transition ${
                    selectedPair.symbol === market.symbol
                      ? "border-cyan-400 bg-cyan-500/10 shadow-[0_0_0_1px_rgba(34,211,238,0.08)]"
                      : "border-slate-200 bg-slate-50 hover:border-slate-400 dark:border-slate-800 dark:bg-slate-900/60"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-slate-950 dark:text-white">
                        {market.displaySymbol}
                      </div>
                      <div className="text-sm text-slate-500 dark:text-slate-400">
                        {market.name}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-medium text-slate-950 dark:text-white">
                        {formatMoney(market.price)}
                      </div>
                      <div
                        className={
                          market.change24h >= 0 ? "text-emerald-500" : "text-rose-500"
                        }
                      >
                        {market.change24h.toFixed(2)}%
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Markets;
